/**
 * PlanDetailsModal — Premium plan pricing modal (redesigned Feb 2026).
 *
 * ⚠️ LOCKED CONTRACT (do not change these — WhatsApp deep links,
 * dashboard/preview/billing triggers, and testing agents depend on them):
 *   - File path: this file (imported as PlanDetailsModal)
 *   - Named export: `PlanDetailsModal`
 *   - Props: { user, onClose, onPaymentSuccess, waContext }
 *   - data-testid hooks preserved: pricing-modal, pricing-close-btn,
 *     plan-cards, plan-card-{id}, plan-badge-{id}, plan-cta-{id},
 *     coupon-input, coupon-apply-btn, coupon-error, coupon-success,
 *     scale-contact-message, scale-contact-close-btn
 *   - Analytics events preserved: PricingPageView, PlanModalOpen,
 *     UpgradePopupShown, CheckoutStarted, PurchaseCompleted
 *   - WA events preserved: checkout_started_from_wa, purchase_completed_from_wa
 *   - Razorpay flow untouched: createOrder → Razorpay checkout → verify
 *   - Coupon flow (Scale only) untouched
 *   - Plan filter logic untouched (free→3 / starter→growth+scale / growth→scale / scale-exhausted→contact)
 */
import { useState, useEffect, useRef } from "react";
import {
  X, ArrowRight, Check, Star, Image as ImageIcon, Users2, ShieldCheck, Zap,
  ChevronDown, Sparkles, Download, Upload, Lock,
} from "lucide-react";
import { paymentAPI, authAPI } from "@/lib/api";
import { trackInternalEvent } from "@/lib/analytics";
import { toast } from "sonner";

const PLAN_PRICES = { starter: 249, growth: 899, scale: 1499 };

const PLANS = [
  {
    id: "growth",
    name: "Growth Pack",
    price: 899,
    photos: 50,
    perPhoto: "18",
    subline: "Turn your menu into a high-converting asset",
    icon: Star,
    badge: "MOST RESTAURANTS CHOOSE",
    features: [
      { text: "Real Food Photos, Not AI Generated" },
      { text: "50 high-conversion food photos ", bold: "to increase your sales" },
      { text: "Consistent premium look across your menu" },
      { text: "", bold: "Unlock all premium photos" },
      { text: "No photoshoot cost. No delays." },
    ],
    ctaLabel: "Unlock 50 Photos",
  },
  {
    id: "starter",
    name: "Starter Pack",
    price: 249,
    photos: 10,
    perPhoto: "25",
    subline: "Ideal for a few dishes or getting started",
    icon: Sparkles,
    features: [
      "10 professionally shot real food photos (Not AI Generated)",
      "Perfect for testing your bestselling dishes",
      "Pre-approved by Zomato & Swiggy",
      "Instant download. Instant upload.",
    ],
    ctaLabel: "Unlock 10 Photos",
  },
  {
    id: "scale",
    name: "Scale Pack",
    price: 1499,
    photos: 200,
    perPhoto: "7.5",
    subline: "For complete menu coverage & brand consistency",
    icon: Zap,
    features: [
      "200 professionally shot real food photos (Not AI Generated)",
      "Complete menu coverage with one consistent brand look",
      "Unlock all premium photos",
      "One-time investment. No reshoots ever.",
    ],
    ctaLabel: "Unlock 200 Photos",
  },
];

const TRUST_POINTERS = [
  {
    icon: ImageIcon,
    lines: ["Real Photos"],
    sub: <>(<span className="text-[#ff3d00] font-semibold">Not AI Generated</span>)</>,
  },
  {
    icon: Users2,
    lines: [<>Trusted by</>, <><span className="text-[#ff3d00] font-semibold">1650+</span></>, "restaurants", "across India"],
  },
  {
    icon: ShieldCheck,
    lines: [<>Pre Approved by</>, <><span className="text-[#ed1638] font-semibold">Zomato</span> & <span className="text-[#ff5a00] font-semibold">Swiggy</span></>],
  },
  {
    icon: Download,
    lines: [<>Download <span className="text-[#ff3d00] font-semibold">Free</span></>, <><span className="text-[#ff3d00] font-semibold">Samples</span> and Verify</>],
  },
];

export function PlanDetailsModal({ user, onClose, onPaymentSuccess, waContext }) {
  const [loading, setLoading] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [expandedId, setExpandedId] = useState("growth");
  const scrollRef = useRef(null);

  const userPlanId = user?.plan_id || null;
  const downloadsUsed = user?.downloads_used ?? 0;
  const downloadLimit = user?.free_download_limit ?? 2;
  const isAtLimit = downloadsUsed >= downloadLimit;
  const isScaleExhausted = userPlanId === "scale" && isAtLimit;

  // Filter plans based on current plan (preserved logic)
  const visiblePlans = PLANS.filter((p) => {
    if (!userPlanId || userPlanId === "free") return true;
    if (userPlanId === "starter") return p.id !== "starter";
    if (userPlanId === "growth") return p.id === "scale";
    return false;
  });

  // Choose default expanded card: prefer growth if visible, else first visible
  useEffect(() => {
    if (isScaleExhausted) return;
    const growth = visiblePlans.find((p) => p.id === "growth");
    setExpandedId(growth ? "growth" : visiblePlans[0]?.id || null);
  }, []);

  // Analytics on mount (preserved)
  useEffect(() => {
    trackInternalEvent("PricingPageView", {
      user_id: user?.user_id,
      metadata: { current_plan: userPlanId, downloads_used: downloadsUsed },
    });
    trackInternalEvent("PlanModalOpen", {
      user_id: user?.user_id,
      metadata: { current_plan: userPlanId, at_limit: isAtLimit },
    });
    trackInternalEvent("UpgradePopupShown", {
      user_id: user?.user_id,
      metadata: { source: "plan_details_modal", current_plan: userPlanId, at_limit: isAtLimit },
    });
  }, []);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleToggle = (planId) => {
    setExpandedId((cur) => (cur === planId ? null : planId));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    setCouponError("");
    setCouponApplied(null);
    try {
      const res = await paymentAPI.validateCoupon("scale", couponCode.trim());
      const d = res.data;
      setCouponApplied({
        discount: d.discount_amount / 100,
        final: d.final_amount / 100,
        discount_value: d.discount_value,
        discount_type: d.discount_type,
      });
    } catch (err) {
      setCouponError(err.response?.data?.detail || "Invalid coupon code");
    }
    setApplyingCoupon(false);
  };

  const handlePurchase = async (planId) => {
    setLoading(planId);
    setCouponError("");

    trackInternalEvent("CheckoutStarted", {
      user_id: user?.user_id,
      product_id: planId,
      plan_name: PLANS.find((p) => p.id === planId)?.name || planId,
      order_value: PLAN_PRICES[planId],
      metadata: { current_plan: userPlanId, coupon: couponCode.trim() || null },
    });

    if (waContext?.wa_token) {
      authAPI.trackWaEvent(waContext.wa_token, "checkout_started_from_wa", { plan_id: planId }).catch(() => {});
    }

    try {
      const applyCoupon = planId === "scale" && couponCode.trim() ? couponCode.trim() : null;
      const res = await paymentAPI.createOrder(planId, applyCoupon);
      const orderData = res.data;

      if (orderData.discount_amount > 0) {
        setCouponApplied({ discount: orderData.discount_amount / 100, final: orderData.amount / 100 });
      }

      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Studio Pass 2.0",
        description: orderData.plan_name,
        order_id: orderData.order_id,
        prefill: { contact: user?.phone || "" },
        theme: { color: "#ff5a00" },
        handler: async (response) => {
          try {
            await paymentAPI.verify({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            const finalAmount = (orderData.amount || 0) / 100 || PLAN_PRICES[planId];
            trackInternalEvent("PurchaseCompleted", {
              user_id: user?.user_id,
              product_id: planId,
              plan_name: PLANS.find((p) => p.id === planId)?.name || planId,
              order_value: finalAmount,
              meta_event_id: response.razorpay_payment_id,
              metadata: {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                coupon: couponApplied ? couponCode.trim() : null,
                discount: couponApplied?.discount || 0,
              },
            });
            if (waContext?.wa_token) {
              authAPI.trackWaEvent(waContext.wa_token, "purchase_completed_from_wa", {
                purchased_plan: planId,
                purchase_amount: finalAmount,
                razorpay_payment_id: response.razorpay_payment_id,
              }).catch(() => {});
            }
            toast.success("Payment successful!");
            onClose();
            if (onPaymentSuccess) onPaymentSuccess();
          } catch {
            toast.error("Payment verification failed");
          }
        },
        modal: { ondismiss: () => setLoading(null) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setLoading(null);
      });
      rzp.open();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to initiate payment";
      if (couponCode.trim() && (detail.includes("coupon") || detail.includes("Coupon"))) {
        setCouponError(detail);
      } else {
        toast.error(detail);
      }
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-0 sm:p-4" data-testid="pricing-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div
        ref={scrollRef}
        className="relative z-10 w-full sm:max-w-[520px] rounded-none sm:rounded-2xl bg-white overflow-y-auto max-h-screen sm:max-h-[92vh] animate-in fade-in zoom-in-95 duration-200"
        style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.25)" }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center z-20 hover:bg-slate-100 transition-colors"
          data-testid="pricing-close-btn"
        >
          <X className="h-5 w-5 text-slate-500" />
        </button>

        <div className="px-5 sm:px-7 pt-8 pb-6">
          {/* Eyebrow with side lines */}
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="h-px w-6 sm:w-10 bg-[#ff5a00]" />
            <p className="text-[10px] sm:text-[11px] font-bold tracking-[0.14em] uppercase text-[#ff5a00]">
              Need More Than Your Free Samples?
            </p>
            <span className="h-px w-6 sm:w-10 bg-[#ff5a00]" />
          </div>

          {/* Headline */}
          <h2
            className="text-center text-[28px] sm:text-[34px] font-extrabold text-slate-900 leading-[1.08] tracking-tight mb-5"
            style={{ fontFamily: "Inter, system-ui, sans-serif" }}
          >
            Unlock Professional<br />Menu Photos
          </h2>

          {/* 4 TRUST POINTERS — compact size to reduce competition with the pricing cards */}
          <section className="grid grid-cols-4 gap-1 mb-6" aria-label="Trust signals">
            {TRUST_POINTERS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="text-center min-w-0">
                  <div className="mx-auto mb-1.5 w-[34px] h-[34px] rounded-full grid place-items-center" style={{ background: "#fff2eb" }}>
                    <Icon className="w-[15px] h-[15px] text-[#ff5a00]" />
                  </div>
                  <div className="text-[9.5px] sm:text-[10px] text-slate-800 leading-[1.35]">
                    {p.lines.map((line, li) => (
                      <div key={li} className="font-semibold">{line}</div>
                    ))}
                    {p.sub && <div className="mt-0.5 font-normal">{p.sub}</div>}
                  </div>
                </div>
              );
            })}
          </section>

          {isScaleExhausted ? (
            <ScaleExhaustedCard onClose={onClose} />
          ) : (
            <div className="space-y-3" data-testid="plan-cards">
              {visiblePlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  expanded={expandedId === plan.id}
                  onToggle={() => handleToggle(plan.id)}
                  onPurchase={() => handlePurchase(plan.id)}
                  loading={loading === plan.id}
                  disabled={!!loading}
                  couponProps={
                    plan.id === "scale"
                      ? {
                          couponCode, setCouponCode,
                          couponError, setCouponError,
                          couponApplied, setCouponApplied,
                          applyingCoupon, handleApplyCoupon,
                        }
                      : null
                  }
                />
              ))}
            </div>
          )}

          {/* Bottom trust line */}
          <div className="mt-6 pt-5 border-t border-slate-200/70 text-center">
            <div className="inline-flex items-center gap-1.5 text-[13px] text-slate-700">
              <Users2 className="w-4 h-4 text-[#ff5a00]" />
              <span>Used by <span className="font-bold">1650+ restaurants</span> across India</span>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">Real food photos. No AI. No rejection issues.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Sub-components ─────────────────── */

function ScaleExhaustedCard({ onClose }) {
  return (
    <div className="text-center py-6 rounded-2xl bg-[#fff8f2] border border-[#ffd9c2]" data-testid="scale-contact-message">
      <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-white">
        <Sparkles className="h-6 w-6 text-[#ff5a00]" />
      </div>
      <p className="text-sm leading-relaxed mb-5 text-slate-700 px-6">
        Call us at <span className="font-bold text-slate-900">9211622662</span><br />
        or email us at <span className="font-bold text-slate-900">studio@foodphotos.in</span><br />
        to unlock bulk download plans.
      </p>
      <button
        onClick={onClose}
        className="h-10 px-8 rounded-full text-sm font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
        data-testid="scale-contact-close-btn"
      >
        Got it
      </button>
    </div>
  );
}

function PlanCard({ plan, expanded, onToggle, onPurchase, loading, disabled, couponProps }) {
  const Icon = plan.icon;
  const isGrowth = plan.id === "growth";

  return (
    <div
      className={`rounded-2xl overflow-hidden transition-all duration-[220ms] ease-out ${
        expanded && isGrowth
          ? "border-2 border-[#ff8a3d]"
          : expanded
          ? "border border-slate-300"
          : "border border-slate-200"
      }`}
      style={{
        background: expanded && isGrowth
          ? "linear-gradient(180deg, #fff5eb 0%, #fff9f3 100%)"
          : "white",
        boxShadow: expanded ? "0 6px 16px rgba(15,23,42,0.06)" : "none",
      }}
      data-testid={`plan-card-${plan.id}`}
    >
      {/* Badge — Growth-only, always at the top when expanded */}
      {expanded && plan.badge && (
        <div className="flex justify-center pt-4">
          <div
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[11px] font-bold tracking-wide text-white"
            style={{ background: "linear-gradient(90deg, #ff8a3d 0%, #ff5a00 100%)" }}
            data-testid={`plan-badge-${plan.id}`}
          >
            <Star className="w-3 h-3 fill-white" />
            {plan.badge}
          </div>
        </div>
      )}

      {!expanded ? (
        /* ─────── COLLAPSED ─────── */
        <button
          type="button"
          onClick={onToggle}
          className="w-full text-left"
          data-testid={`plan-card-toggle-${plan.id}`}
        >
          <div className="flex items-center gap-3 px-4 py-4">
            <div className="w-11 h-11 shrink-0 rounded-full grid place-items-center" style={{ background: "#fff2eb" }}>
              <Icon className="w-5 h-5 text-[#ff5a00]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-slate-900">{plan.name}</p>
              <p className="text-[20px] font-extrabold text-slate-900 leading-tight">{plan.photos} Photos</p>
              <p className="text-[15px] font-bold text-[#ff5a00] leading-tight mt-0.5">₹{plan.price}</p>
              <p className="text-[10.5px] text-slate-500 mt-0.5">₹{plan.perPhoto} per photo</p>
            </div>
            {/* Orange-highlighted chevron pulls the eye to the expand affordance
                so users don't miss the other plans. */}
            <div
              className="w-9 h-9 shrink-0 rounded-full grid place-items-center transition-transform duration-200 group-hover:scale-105"
              style={{
                background: "linear-gradient(135deg, #ff8a3d 0%, #ff5a00 100%)",
                boxShadow: "0 3px 10px rgba(255,90,0,0.28)",
              }}
              aria-hidden
            >
              <ChevronDown className="w-4 h-4 text-white" strokeWidth={2.8} />
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-4 pb-3 -mt-1 text-[12px] text-slate-500">
            <Sparkles className="w-3.5 h-3.5 text-[#ff5a00]" />
            <span>{plan.subline}</span>
          </div>
        </button>
      ) : (
        /* ─────── EXPANDED — IDENTICAL LAYOUT FOR ALL PLANS ─────── */
        <div className="px-5 pt-5 pb-5 animate-in fade-in duration-[220ms]">
          <div className="text-center">
            <p className="text-[13px] font-semibold text-slate-500 mb-1">{plan.name}</p>
            <p className="text-[36px] sm:text-[40px] font-extrabold text-slate-900 leading-none tracking-tight">
              {plan.photos} Photos
            </p>
            <p className="mt-3 text-[26px] sm:text-[28px] font-extrabold text-[#ff5a00] leading-none">
              ₹{plan.price}
            </p>
            <p className="mt-1.5 text-[11px] text-slate-500">₹{plan.perPhoto} per photo</p>
          </div>

          <div className="my-4 h-px bg-slate-200/70" />

          <ul className="space-y-2.5 mb-4">
            {plan.features.map((f, i) => {
              const isObj = typeof f === "object";
              const text = isObj ? f.text : f;
              const bold = isObj ? f.bold : null;
              return (
                <li key={i} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 shrink-0 mt-[3px] text-[#ff5a00]" strokeWidth={2.5} />
                  <span className={`text-[13.5px] leading-snug text-slate-800 ${!text && bold ? "font-bold" : ""}`}>
                    {text}
                    {bold && <span className="font-bold">{bold}</span>}
                  </span>
                </li>
              );
            })}
          </ul>

          {/* Coupon (Scale only, kept in same slot for identical rhythm) */}
          {couponProps && <CouponBox {...couponProps} />}

          {/* CTA — same orange primary treatment for every plan */}
          <button
            onClick={onPurchase}
            disabled={disabled}
            data-testid={`plan-cta-${plan.id}`}
            className="w-full h-12 flex items-center justify-center gap-2 rounded-full text-[15px] font-bold text-white transition-all duration-200 disabled:opacity-60"
            style={{
              background: "linear-gradient(90deg, #ff8a3d 0%, #ff5a00 100%)",
              boxShadow: "0 6px 18px rgba(255,90,0,0.28)",
            }}
          >
            {loading ? "Processing..." : plan.ctaLabel}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>

          {/* Micro trust line — present on ALL expanded plans (not just Growth) */}
          <div className="mt-3.5 flex items-center justify-center gap-2 flex-wrap text-[11.5px] text-slate-500">
            <span className="inline-flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-[#ff5a00]" />Instant download</span>
            <span className="text-slate-300">•</span>
            <span className="inline-flex items-center gap-1"><Upload className="w-3.5 h-3.5 text-[#ff5a00]" />Instant upload</span>
            <span className="text-slate-300">•</span>
            <span className="inline-flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-[#ff5a00]" />Safe &amp; secure</span>
          </div>

          {/* Collapse handle — only for non-Growth expanded plans, since Growth
              is the default anchor and should not tempt users to close it. */}
          {!isGrowth && (
            <button
              type="button"
              onClick={onToggle}
              className="w-full mt-3 text-[12px] text-slate-500 hover:text-slate-700 transition-colors"
              data-testid={`plan-collapse-${plan.id}`}
            >
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CouponBox({
  couponCode, setCouponCode, couponError, setCouponError,
  couponApplied, setCouponApplied, applyingCoupon, handleApplyCoupon,
}) {
  return (
    <div className="mb-4">
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Coupon code"
          value={couponCode}
          onChange={(e) => {
            setCouponCode(e.target.value.toUpperCase());
            setCouponError("");
            setCouponApplied(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
          className="flex-1 h-10 px-3 rounded-lg text-[13px] font-medium bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#ff5a00]/60"
          data-testid="coupon-input"
        />
        <button
          onClick={handleApplyCoupon}
          disabled={!couponCode.trim() || applyingCoupon}
          className="h-10 px-4 rounded-lg text-[12px] font-bold border border-[#ff5a00]/30 text-[#ff5a00] bg-[#fff2eb] hover:bg-[#ffe5d5] transition-colors disabled:opacity-40"
          data-testid="coupon-apply-btn"
        >
          {applyingCoupon ? "..." : "Apply"}
        </button>
      </div>
      {couponError && <p className="text-[11px] mt-1.5 text-red-500" data-testid="coupon-error">{couponError}</p>}
      {couponApplied && (
        <div className="mt-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="coupon-success">
          <p className="text-[12px] font-semibold text-emerald-700">
            {couponApplied.discount_type === "percentage"
              ? `${couponApplied.discount_value}% off`
              : `₹${couponApplied.discount} off`} applied!
          </p>
          <p className="text-[11px] text-emerald-600/80">
            <span className="line-through">₹1499</span> → <span className="font-bold text-emerald-700">₹{couponApplied.final}</span>
          </p>
        </div>
      )}
    </div>
  );
}
